/**
 * API de las páginas de mercado portadas del ETRM (22-sep-2026): Mapa de
 * precios, Compras MDA, Ofertas de capacidad (MTR) y Margen capacidad − demanda.
 * Va en su propio módulo para no chocar con los tipos de api.ts.
 */
import { detalleDe, fetchAuth, jsonFetch } from "./api";

async function jsonPut<T>(url: string, body: unknown): Promise<T> {
  const res = await fetchAuth(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

async function jsonDelete<T>(url: string): Promise<T> {
  const res = await fetchAuth(url, { method: "DELETE" });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}


// ---------- Ofertas de capacidad al MTR (reporte + Excel) --------------------

export type OfertasCapacidadFila = {
  sistema: string;
  fecha: string;
  hora: number;
  term_bloques_mas_pmin_mw: number | null;
  hidro_pmax_mw: number | null;
  interm_mw_bloque_01: number | null;
  nodesp_pot_media_mw: number | null;
  /** suma de las 4 columnas de MW (nulos cuentan como 0) */
  total_mw: number | null;
};

export type OfertasCapacidad = {
  desde: string;
  hasta: string;
  sistemas: string[];
  columnas: { key: string; label: string }[];
  filas: OfertasCapacidadFila[];
  truncado: boolean;
  limite: number;
  disponible: { ini: string | null; fin: string | null };
};

/** Primer y último mes con datos (acota los selectores de la página). */
export type OfertasCapacidadRango = {
  ini: string | null;
  fin: string | null;
  mes_ini: string | null; // "2017-01"
  mes_fin: string | null; // "2026-06"
};

export function getOfertasCapacidadRango(): Promise<OfertasCapacidadRango> {
  return jsonFetch<OfertasCapacidadRango>("/api/ofertas-capacidad/rango");
}

export function getOfertasCapacidad(
  desde: string,
  hasta: string,
  sistemas: string[],
): Promise<OfertasCapacidad> {
  const p = new URLSearchParams({ desde, hasta, sistemas: sistemas.join(",") });
  return jsonFetch<OfertasCapacidad>(`/api/ofertas-capacidad?${p.toString()}`);
}

// ---- Margen: capacidad total ofertada − demanda real (top-N más apretado) --

export type MargenCapacidadFila = {
  sistema: string;
  fecha: string;
  hora: number;
  capacidad_mw: number;
  intercambio_mw: number | null; // solo viene con "incluir +import − export"
  demanda_mw: number;
  margen_mw: number;
  margen_pct: number | null; // margen ÷ capacidad total ofertada × 100 (null si capacidad 0)
  // MRO de CENACE (margen_reserva_operativa, una fila por sistema y día).
  mro_hora_pico: string | null; // "HH:MM" del pico de demanda instantánea; en todas las filas del día
  mro_dem_max_mw_dia: number | null; // demanda máx. instantánea DEL DÍA; en todas las filas del día (tooltips)
  mro_reserva_mw_dia: number | null; // reserva operativa DEL DÍA; en todas las filas del día (gráfica)
  mro_reserva_pct_dia: number | null; // ídem en %; null solo si el día no tiene registro MRO
  mro_dem_max_mw: number | null; // SOLO en la fila cuya hora contiene el pico
  mro_reserva_mw: number | null; // ídem
  mro_reserva_pct: number | null; // ídem; reserva ÷ demanda máx. instantánea × 100 (no ÷ capacidad)
  fuente_demanda: "balance" | "horaria";
};

export type MargenCapacidad = {
  anio: number;
  sistemas: string[];
  top: number;
  intercambio: boolean;
  columnas: { key: string; label: string }[];
  filas: MargenCapacidadFila[];
};

export function getMargenCapacidad(
  anio: number,
  sistemas: string[],
  top = 100,
  intercambio = false,
): Promise<MargenCapacidad> {
  const p = new URLSearchParams({
    anio: String(anio), sistemas: sistemas.join(","), top: String(top),
    intercambio: String(intercambio),
  });
  return jsonFetch<MargenCapacidad>(`/api/ofertas-capacidad/margen?${p.toString()}`);
}

export async function descargarMargenCapacidadXlsx(
  anio: number,
  sistemas: string[],
  top = 100,
  intercambio = false,
): Promise<void> {
  const p = new URLSearchParams({
    anio: String(anio), sistemas: sistemas.join(","), top: String(top),
    intercambio: String(intercambio),
  });
  const res = await fetchAuth(`/api/ofertas-capacidad/margen/xlsx?${p.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `margen_capacidad_demanda_${anio}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/** Descarga el corte como .xlsx (mismos filtros que la tabla). */
export async function descargarOfertasCapacidadXlsx(
  desde: string,
  hasta: string,
  sistemas: string[],
): Promise<void> {
  const p = new URLSearchParams({ desde, hasta, sistemas: sistemas.join(",") });
  const res = await fetchAuth(`/api/ofertas-capacidad/xlsx?${p.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `ofertas_capacidad_${desde}_${hasta}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---- Dispersión de horas críticas: TODAS las horas del año + marca HC ------

/** Una hora del año (claves compactas: viajan ~8,760 filas por sistema). */
export type DispersionHcFila = {
  s: string;            // sistema
  f: string;            // fecha ISO
  h: number;            // hora 1–24
  cap: number;          // capacidad total ofertada (MW)
  int: number | null;   // intercambio neto, solo con "incluir +import − export"
  dem: number;          // demanda real (MW)
  mw: number;           // margen = capacidad (+intercambio) − demanda
  pct: number | null;   // margen ÷ capacidad × 100
  fd: "balance" | "horaria";
  hc: number | null;    // posición como hora crítica (1 = la más apretada) o null
  hcr: number | null;   // # de hora crítica DEFINITIVA de CENACE, o null si no lo es
};

/** Cuántas de las HC calculadas coinciden con la lista definitiva de CENACE. */
export type ValidacionHc = {
  reales: number;     // HC publicadas por CENACE para ese sistema y año
  coinciden: number;  // de las nuestras, cuántas están en esa lista
  fuera: number;      // HC de CENACE que nuestro top-N no alcanzó
};

export type VentanaHc = { desde: string; hasta: string };

/** Periodo HC de CENACE de un año. CENACE define UNA VENTANA POR SISTEMA, así que
 *  `sistemas` trae la de cada uno; un sistema sin ventana no marca HC.
 *  fuente: capturado ('configuracion'), respaldo en código ('default') o null. */
export type PeriodoHc = {
  anio: number;
  sistemas: Record<string, VentanaHc>;
  fuente: "configuracion" | "default" | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
};

export type DispersionHc = {
  anio: number;
  sistemas: string[];
  top: number;
  intercambio: boolean;
  periodo: Omit<PeriodoHc, "anio">;
  hay_reales: boolean;                          // CENACE ya publicó el año
  validacion: Record<string, ValidacionHc>;     // por sistema
  filas: DispersionHcFila[];
};

export function getPeriodoHc(anio: number): Promise<PeriodoHc> {
  return jsonFetch<PeriodoHc>(`/api/ofertas-capacidad/periodo-hc?anio=${anio}`);
}

export function getDispersionHc(
  anio: number,
  sistemas: string[],
  top = 100,
  intercambio = false,
): Promise<DispersionHc> {
  const p = new URLSearchParams({
    anio: String(anio), sistemas: sistemas.join(","), top: String(top),
    intercambio: String(intercambio),
  });
  return jsonFetch<DispersionHc>(`/api/ofertas-capacidad/dispersion?${p.toString()}`);
}

// ---- Periodo de horas críticas (captura; en el Portal no hay perfiles) -----

export type PeriodoHcFila = {
  anio: number;
  fuente: "configuracion" | "default";
  sistemas: Record<string, VentanaHc>;
};
export type PeriodosHc = {
  periodos: PeriodoHcFila[];
  actualizado_en: string | null;
  actualizado_por: string | null;
  tabla_lista: boolean;
};

export function getPeriodosHc(): Promise<PeriodosHc> {
  return jsonFetch<PeriodosHc>("/api/ofertas-capacidad/periodo-hc/todos");
}
export function setPeriodoHc(
  payload: { anio: number; sistema: string; desde: string; hasta: string },
): Promise<PeriodosHc> {
  return jsonPut("/api/ofertas-capacidad/periodo-hc", payload);
}
/** Sin `sistema` borra el año completo; con él, solo esa ventana. */
export function borrarPeriodoHc(anio: number, sistema?: string): Promise<PeriodosHc> {
  const q = sistema ? `?sistema=${encodeURIComponent(sistema)}` : "";
  return jsonDelete(`/api/ofertas-capacidad/periodo-hc/${anio}${q}`);
}

// ---------- Mapa de precios (PML nodos / PND zonas) --------------------------

/** Con qué se ubicó un nodo en el mapa, del más preciso al más aproximado. */
export type MapaUbicacion = "subestacion" | "localidad" | "cabecera" | "zona";

export type MapaMarcador = {
  clave: string;
  nombre: string;
  sistema: string;
  zona?: string;
  entidad: string | null;
  municipio?: string | null;
  n_nodos?: number;
  lat: number;
  lon: number;
  precio: number | null; // promedio del último día publicado ($/MWh)
  ubicacion?: MapaUbicacion;        // solo nodos (las zonas son el promedio de sus nodos)
  ubicacion_nombre?: string | null; // subestación / localidad / cabecera de referencia
};

export type MapaMarcadores = {
  mercado: "mda" | "mtr";
  fecha: string | null; // último día publicado
  unidad: string;
  items: MapaMarcador[];
};

export type MapaMercadoStats = {
  /** Promedio de las 24 horas del último día publicado del mercado. */
  ultimo: { fecha: string; precio: number | null } | null;
  promedio_anio: number | null;
};

export type MapaDetalle = {
  tipo: "pml" | "pnd";
  clave: string;
  nombre: string;
  sistema: string;
  zona: string;
  entidad: string | null;
  municipio?: string | null;
  tension_kv?: number | null;
  n_nodos?: number;
  ubicacion?: MapaUbicacion;
  ubicacion_nombre?: string | null;
  unidad: string;
  anio: number | null;
  mercados: { mda: MapaMercadoStats; mtr: MapaMercadoStats };
  perfil_hora: { hora: number; mda: number | null; mtr: number | null }[];
  serie7d: { t: string; fecha: string; hora: number; mda: number | null; mtr: number | null }[];
};

export function getMapaMarcadores(modo: "pml" | "pnd", mercado: "mda" | "mtr") {
  const ep = modo === "pml" ? "nodos" : "zonas";
  return jsonFetch<MapaMarcadores>(`/api/mapa/${ep}?mercado=${mercado}`);
}

export function getMapaDetalle(modo: "pml" | "pnd", clave: string) {
  return jsonFetch<MapaDetalle>(`/api/mapa/detalle/${modo}/${encodeURIComponent(clave)}`);
}

// ---------- Modo Operación: conectividad por la red de transmisión (OSM) ------

/** Nodo de precios que vive en una subestación alcanzada. */
export type MapaConectNodo = {
  clave: string;
  nombre: string;
  sistema: string;
  zona: string | null;
  tension_kv: number | null;
};

/** Subestación de la red OSM alcanzada desde el nodo elegido. */
export type MapaConectSub = {
  id: string;               // "way/123" | "relation/9" | "node/77" (id de OpenStreetMap)
  nombre: string | null;
  lat: number;
  lon: number;
  kv: number[];             // tensiones de la subestación según OSM
  salto: number;            // 0 = la subestación del nodo elegido
  km: number | null;        // longitud del enlace por el que se llegó
  via: string | null;       // nombre/ref de la(s) línea(s) de ese enlace
  desde: string | null;     // id de la subestación anterior en el recorrido
  nodos: MapaConectNodo[];  // nuestros nodos anclados a esa subestación
};

/** Tramo de línea entre dos subestaciones (b = null: la línea sigue pero OSM
 *  no tiene mapeada la subestación del otro extremo). */
export type MapaConectEnlace = {
  a: string;
  b: string | null;
  kv: number[];
  nombre: string | null;
  km: number;
  salto: number;
  geom: [number, number][][]; // polilíneas [lat, lon] simplificadas
};

export type MapaConectividad = {
  nodo: string;
  saltos: number;
  subestacion: MapaConectSub | null; // null = el nodo no está anclado a una subestación OSM con red
  motivo: string | null;             // por qué no hay recorrido, cuando subestacion es null
  subestaciones: MapaConectSub[];    // incluye la de origen (salto 0), ordenadas por salto y km
  enlaces: MapaConectEnlace[];
  generado: string | null;           // fecha del extracto OSM
};

/** Toda la red del extracto OSM (Modo Operación sin nodo elegido). */
export type MapaRedCompleta = {
  generado: string | null;
  subestaciones: {
    id: string; nombre: string | null; lat: number; lon: number; kv: number[];
    nodos: string[]; // claves de nuestros nodos anclados a la subestación
  }[];
  enlaces: Omit<MapaConectEnlace, "salto">[];
};

export function getMapaRed() {
  return jsonFetch<MapaRedCompleta>("/api/mapa/red");
}

export function getMapaConectividad(clave: string, saltos = 2) {
  return jsonFetch<MapaConectividad>(
    `/api/mapa/conectividad?nodo=${encodeURIComponent(clave)}&saltos=${saltos}`,
  );
}

// ---------- Compras MDA por participante y zona agrupada ---------------------

export type ComprasMdaItem = {
  participante: string;
  nombre: string;
  valores: Record<string, number>;          // zona_carga_agrupada -> MW
  precios: Record<string, number | null>;   // zona -> precio promedio ponderado $/MWh
  total: number;
  precio_prom: number | null;               // ponderado de todas sus compras
};

export type ComprasMdaAgrupacion = "dia" | "mes" | "anio";

export type ComprasMdaReporte = {
  agrupacion: ComprasMdaAgrupacion;
  periodo: string;   // YYYY-MM-DD | YYYY-MM | YYYY
  fecha: string;
  fecha_max: string; // última fecha publicada por CENACE
  grupos: string[];
  items: ComprasMdaItem[];
  total_grupo: Record<string, number>;
  total: number;
  sistema?: string;
};

/** "" = los tres sistemas sumados (SIN + BCA + BCS). */
export type ComprasMdaSistema = "" | "SIN" | "BCA" | "BCS";

export function getComprasMda(
  agrupacion: ComprasMdaAgrupacion,
  fecha?: string,
  sistema?: ComprasMdaSistema,
): Promise<ComprasMdaReporte> {
  const qs = new URLSearchParams({ agrupacion });
  if (fecha) qs.set("fecha", fecha);
  if (sistema) qs.set("sistema", sistema);
  return jsonFetch<ComprasMdaReporte>(`/api/compras-mda?${qs.toString()}`);
}

export type ComprasMdaRap = {
  anio: number;
  sistemas: string[];
  top: number;                 // nº de horas críticas pedidas
  horas: number;               // horas críticas encontradas en el año
  horas_cubiertas: number;     // …de esas, las que ya tienen ofertas publicadas
  desde: string | null;        // primera / última fecha crítica considerada
  hasta: string | null;
  fecha_max: string;
  rap: Record<string, number>; // participante -> MW promedio en horas críticas
  total: number;
};

/**
 * RAP = promedio de compra (MW) de cada participante en las horas críticas
 * (Top-N de menor margen de /margen-capacidad) del AÑO de `fecha`. Es una
 * consulta pesada (~30 s la primera vez del año, luego caché en el backend).
 */
export function getComprasMdaRap(
  fecha?: string,
  sistemas?: string,
): Promise<ComprasMdaRap> {
  const qs = new URLSearchParams();
  if (fecha) qs.set("fecha", fecha);
  if (sistemas) qs.set("sistemas", sistemas);
  const q = qs.toString();
  return jsonFetch<ComprasMdaRap>(`/api/compras-mda/rap${q ? `?${q}` : ""}`);
}

export type ComprasMdaRapHora = {
  pos: number;              // lugar en el top de horas críticas (1 = la más apretada)
  sistema: string;
  fecha: string;
  hora: number;
  margen_mw: number | null; // margen de capacidad de esa hora
  margen_pct: number | null;
  mw: number;               // demanda fija ofertada por el participante en esa hora
};

export type ComprasMdaRapDetalle = {
  anio: number;
  sistemas: string[];
  top: number;
  horas: number;
  horas_cubiertas: number;
  desde: string | null;
  hasta: string | null;
  fecha_max: string;
  participante: string;
  filas: ComprasMdaRapHora[]; // TODAS las horas del top, incluidas las de 0 MW
  suma_mw: number;
  horas_con_compra: number;
  rap: number | null;         // suma_mw ÷ filas.length
};

/** De dónde sale el RAP de un participante: las horas críticas una por una. */
export function getComprasMdaRapDetalle(
  participante: string,
  fecha?: string,
  sistemas?: string,
): Promise<ComprasMdaRapDetalle> {
  const qs = new URLSearchParams({ participante });
  if (fecha) qs.set("fecha", fecha);
  if (sistemas) qs.set("sistemas", sistemas);
  return jsonFetch<ComprasMdaRapDetalle>(
    `/api/compras-mda/rap/detalle?${qs.toString()}`,
  );
}

export type ComprasMdaDetalleFila = {
  zona_carga: string;
  nodo_zona: string;
  es_nodo: boolean;
  codigos: number;
  dias: number;
  mw: number;
  precio_prom: number | null;
};

export type ComprasMdaDetalle = {
  participante: string;
  grupo: string | null;
  agrupacion: ComprasMdaAgrupacion;
  desde: string;
  hasta: string;
  filas: ComprasMdaDetalleFila[];
  total_mw: number;
  precio_prom: number | null;
};

export function getComprasMdaDetalle(opts: {
  participante: string;
  agrupacion: ComprasMdaAgrupacion;
  fecha?: string;
  grupo?: string | null;
  sistema?: ComprasMdaSistema;
}): Promise<ComprasMdaDetalle> {
  const qs = new URLSearchParams({
    participante: opts.participante,
    agrupacion: opts.agrupacion,
  });
  if (opts.fecha) qs.set("fecha", opts.fecha);
  if (opts.grupo) qs.set("grupo", opts.grupo);
  if (opts.sistema) qs.set("sistema", opts.sistema);
  return jsonFetch<ComprasMdaDetalle>(`/api/compras-mda/detalle?${qs.toString()}`);
}


// ---------- Compras MDA: línea de tiempo por contraparte ---------------------

export type ComprasMdaSerieAgrupacion = "hora" | "dia" | "mes";

export type ComprasMdaSerieItem = {
  participante: string;
  nombre: string;
  total: number;
  /** MW por periodo, alineado con `periodos` */
  valores: number[];
};

export type ComprasMdaSerieTotal = {
  participante: string;
  nombre: string;
  total: number;
};

/** Suma de las contrapartes que quedaron fuera de `series` (si se pidió). */
export type ComprasMdaSerieOtros = {
  contrapartes: number;
  total: number;
  valores: number[];
};

export type ComprasMdaSerie = {
  desde: string;
  hasta: string;
  fecha_min: string;
  fecha_max: string;
  agrupacion: ComprasMdaSerieAgrupacion;
  sistema?: string;
  periodos: string[];                 // "YYYY-MM-DD HH" | YYYY-MM-DD | YYYY-MM
  series: ComprasMdaSerieItem[];      // solo las contrapartes graficadas
  otros: ComprasMdaSerieOtros | null; // agregado del resto (con otros=true)
  totales: ComprasMdaSerieTotal[];    // TODAS las del periodo, de mayor a menor
  total: number;
};

/**
 * Evolución de las compras en el MDA entre dos fechas, una serie por
 * contraparte, más el acumulado del periodo de todas ellas (el del pastel).
 *
 * La primera consulta de un rango largo puede tardar (el backend cachea por
 * mes; los meses ya vistos regresan al instante).
 */
export function getComprasMdaSerie(p: {
  desde?: string;
  hasta?: string;
  agrupacion: ComprasMdaSerieAgrupacion;
  sistema?: ComprasMdaSistema;
  participantes?: string[];
  top?: number;
  otros?: boolean;
}): Promise<ComprasMdaSerie> {
  const qs = new URLSearchParams({ agrupacion: p.agrupacion });
  if (p.sistema) qs.set("sistema", p.sistema);
  if (p.desde) qs.set("desde", p.desde);
  if (p.hasta) qs.set("hasta", p.hasta);
  if (p.top) qs.set("top", String(p.top));
  if (p.otros) qs.set("otros", "true");
  for (const x of p.participantes ?? []) qs.append("participantes", x);
  return jsonFetch<ComprasMdaSerie>(`/api/compras-mda/serie?${qs.toString()}`);
}

/** Primera y última fecha publicadas: consulta de centavos, para resolver el
 *  rango en el front antes de pedir datos. */
export function getComprasMdaRangoFechas(): Promise<{
  fecha_min: string;
  fecha_max: string;
}> {
  return jsonFetch("/api/compras-mda/serie/rango");
}
